import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { supabase, isSupabaseConfigured, transformContactToCamelCase } from '../utils/supabase';
import { sortCRMUsersWithDTCPriority } from '../utils/crmUserSorting';
import ContactDetailDrawer from '../components/CRM/ContactDetailDrawer';

/**
 * Contact Drawer Context
 *
 * App-level ContactDetailDrawer so any component (task cards, task modals,
 * widgets) can slide the drawer open on WHATEVER page the user is on, instead
 * of navigating away to /crm/contacts/:contactId (which mounts the CRM
 * dashboard). Mounted once inside the Router in AppRouter.
 *
 * The drawer is only rendered after a contact is loaded, so this provider adds
 * zero fetching/subscription overhead on pages where it is never used.
 *
 * Usage:
 *   const contactDrawer = useContactDrawer();
 *   contactDrawer?.openContactDrawer(contactId);
 */
const ContactDrawerContext = createContext(null);

// Returns null when the provider is not mounted so callers can fall back to
// route navigation.
export function useContactDrawer() {
  return useContext(ContactDrawerContext);
}

export function ContactDrawerProvider({ children }) {
  const toast = useToast();
  const [contact, setContact] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [crmUsers, setCrmUsers] = useState([]);
  const crmUsersLoadedRef = useRef(false);

  // The drawer uses crmUsers for owner display/assignment. Loaded once, and
  // only when the drawer is first opened (same Firestore query DashboardPage
  // uses).
  const loadCrmUsers = useCallback(async () => {
    if (crmUsersLoadedRef.current) return;
    crmUsersLoadedRef.current = true;
    try {
      const usersQuery = query(collection(db, 'users'), where('CRMUser', '==', true));
      const snapshot = await getDocs(usersQuery);
      const users = snapshot.docs.map(docSnap => ({
        uid: docSnap.id,
        name: docSnap.data().Name || docSnap.data().name || docSnap.data().email,
        ...docSnap.data(),
      }));
      setCrmUsers(sortCRMUsersWithDTCPriority(users));
    } catch (error) {
      console.error('[ContactDrawerProvider] Could not load CRM users:', error);
      crmUsersLoadedRef.current = false; // allow retry on the next open
    }
  }, []);

  const openContactDrawer = useCallback(async (contactId) => {
    if (!contactId || !isSupabaseConfigured()) return;
    loadCrmUsers();
    try {
      const { data, error } = await supabase
        .from('crm_contacts')
        .select('*')
        .eq('id', contactId)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Contact does not exist');
      setContact(transformContactToCamelCase(data));
      setIsOpen(true);
    } catch (error) {
      console.error('[ContactDrawerProvider] Error loading contact:', error);
      toast({
        title: 'Contact not found',
        description: 'Could not load contact details',
        status: 'warning',
        duration: 3000,
      });
    }
  }, [loadCrmUsers, toast]);

  const closeContactDrawer = useCallback(() => {
    setIsOpen(false);
    setContact(null);
  }, []);

  return (
    <ContactDrawerContext.Provider value={{ openContactDrawer, closeContactDrawer }}>
      {children}
      {contact && (
        <ContactDetailDrawer
          isOpen={isOpen}
          onClose={closeContactDrawer}
          contact={contact}
          contactId={contact.id}
          onContactUpdated={(updatedContact) => setContact(updatedContact)}
          onContactDeleted={closeContactDrawer}
          crmUsers={crmUsers}
        />
      )}
    </ContactDrawerContext.Provider>
  );
}
